import { combineReducers } from 'redux'

import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'

import {
  CLEAR_VENDOR_CREATE_ERROR,
  CLEAR_VENDOR_IMPORT,
  CLEAR_VENDOR_SELECTION,
  CLEAR_VENDOR_UNDO,
  CREATE_VENDOR_ASYNC,
  DEFAULT_QUERY,
  FETCH_VENDOR_SETTINGS_ASYNC,
  FETCH_VENDOR_SUMMARY_ASYNC,
  FETCH_VENDOR_SYNC_STATUS_ASYNC,
  FETCH_VENDORS_ASYNC,
  IMPORT_VENDORS_ASYNC,
  isTerminalSyncStatus,
  REMOVE_VENDORS_ASYNC,
  RESTORE_VENDORS_ASYNC,
  SET_VENDOR_QUERY,
  START_VENDOR_SYNC_ASYNC,
  TOGGLE_VENDOR,
  TOGGLE_VENDOR_PAGE,
  UPDATE_VENDOR_SETTINGS_ASYNC,
  VENDOR_SYNC_POLL_EXHAUSTED,
} from './constants'
import { vendorErrorMessage } from './errors'

// Narrowing a filter while on page 3 would land the user on an empty table.
const PAGE_RESETTING = ['search', 'authStatus', 'sort', 'order', 'window']
// Remove acts on explicit ids, so changing which rows are visible clears them.
const SELECTION_CLEARING = ['page', 'search', 'authStatus', 'window']

const initialState = {
  query: DEFAULT_QUERY,
  rows: [],
  total: 0,
  summary: null,
  rowsLoading: true,
  rowsError: null,
  summaryError: null,
  selected: [],
  recentVendor: null,
  creating: false,
  createError: null,
  importing: false,
  importResult: null,
  importError: null,
  lastRemoved: null,
  syncing: false,
  autoDetection: null,
  autoDetectionSaving: false,
}

const applyQuery = (state, patch) => {
  const keys = Object.keys(patch)
  const query = { ...state.query, ...patch }
  if (keys.some((key) => PAGE_RESETTING.includes(key))) query.page = 1
  return {
    ...state,
    query,
    selected: keys.some((key) => SELECTION_CLEARING.includes(key)) ? [] : state.selected,
    recentVendor: null,
  }
}

const vendorProtectionReducer = (state = initialState, action) => {
  switch (action.type) {
    // A partner switching tenant: ids and rows from the previous one do not carry over.
    case COMPANY_FILTER_CHANGE:
      return {
        ...state,
        recentVendor: null,
        selected: [],
        lastRemoved: null,
        syncing: false,
        autoDetection: null,
        autoDetectionSaving: false,
      }

    case SET_VENDOR_QUERY:
      return applyQuery(state, action.payload || {})

    case FETCH_VENDORS_ASYNC.INITIATE:
      return { ...state, rowsLoading: true }
    case FETCH_VENDORS_ASYNC.SUCCESS:
      return {
        ...state,
        rows: action.payload?.items ?? [],
        total: action.payload?.total ?? 0,
        rowsError: null,
        rowsLoading: false,
      }
    case FETCH_VENDORS_ASYNC.ERROR:
      return {
        ...state,
        rows: [],
        total: 0,
        rowsError: vendorErrorMessage(action.error, 'Failed to load vendors.'),
        rowsLoading: false,
      }

    case FETCH_VENDOR_SUMMARY_ASYNC.SUCCESS:
      return { ...state, summary: action.payload ?? null, summaryError: null }
    case FETCH_VENDOR_SUMMARY_ASYNC.ERROR:
      return {
        ...state,
        summary: null,
        summaryError: vendorErrorMessage(action.error, 'Failed to load vendor totals.'),
      }

    // A failed read leaves the switch unknown, so it stays disabled rather than guessing.
    case FETCH_VENDOR_SETTINGS_ASYNC.SUCCESS:
      return { ...state, autoDetection: action.payload?.autoDetectionEnabled ?? null }

    case UPDATE_VENDOR_SETTINGS_ASYNC.INITIATE:
      return { ...state, autoDetectionSaving: true }
    // Only the service's answer moves the switch, so a failed save leaves it where it was.
    case UPDATE_VENDOR_SETTINGS_ASYNC.SUCCESS:
      return {
        ...state,
        autoDetection: action.payload?.autoDetectionEnabled ?? state.autoDetection,
        autoDetectionSaving: false,
        // Ids picked before the change may now belong to hidden vendors.
        selected: [],
      }
    case UPDATE_VENDOR_SETTINGS_ASYNC.ERROR:
      return { ...state, autoDetectionSaving: false }

    case CREATE_VENDOR_ASYNC.INITIATE:
      return { ...state, creating: true, createError: null }
    case CREATE_VENDOR_ASYNC.SUCCESS:
      // Runs after the epic's SET_VENDOR_QUERY, which resets recentVendor.
      return { ...state, creating: false, recentVendor: action.payload?.vendor ?? null }
    case CREATE_VENDOR_ASYNC.ERROR:
      return {
        ...state,
        creating: false,
        createError: vendorErrorMessage(action.error, 'Failed to add the vendor.'),
      }
    case CLEAR_VENDOR_CREATE_ERROR:
      return { ...state, createError: null }

    case IMPORT_VENDORS_ASYNC.INITIATE:
      return { ...state, importing: true, importResult: null, importError: null }
    case IMPORT_VENDORS_ASYNC.SUCCESS:
      return { ...state, importing: false, importResult: action.payload ?? null }
    case IMPORT_VENDORS_ASYNC.ERROR:
      return {
        ...state,
        importing: false,
        importError: vendorErrorMessage(action.error, 'Failed to import the file.'),
      }
    case CLEAR_VENDOR_IMPORT:
      return { ...state, importing: false, importResult: null, importError: null }

    case START_VENDOR_SYNC_ASYNC.INITIATE:
      return { ...state, syncing: true }
    case START_VENDOR_SYNC_ASYNC.ERROR:
      return { ...state, syncing: false }
    // Every poll lands here, so the terminal answer is what ends the spinner.
    case FETCH_VENDOR_SYNC_STATUS_ASYNC.SUCCESS:
      return { ...state, syncing: !isTerminalSyncStatus(action.payload?.status) }
    case FETCH_VENDOR_SYNC_STATUS_ASYNC.ERROR:
    case VENDOR_SYNC_POLL_EXHAUSTED:
      return { ...state, syncing: false }

    case REMOVE_VENDORS_ASYNC.SUCCESS:
      return { ...state, selected: [], lastRemoved: action.payload?.vendors ?? null }
    case RESTORE_VENDORS_ASYNC.SUCCESS:
      return { ...state, selected: [], lastRemoved: null }
    case CLEAR_VENDOR_UNDO:
      return { ...state, lastRemoved: null }

    case TOGGLE_VENDOR: {
      const id = action.payload
      return {
        ...state,
        selected: state.selected.includes(id)
          ? state.selected.filter((item) => item !== id)
          : [...state.selected, id],
      }
    }
    case TOGGLE_VENDOR_PAGE: {
      const pageIds = action.payload || []
      const allChosen = pageIds.length > 0 && pageIds.every((id) => state.selected.includes(id))
      return {
        ...state,
        selected: allChosen ? state.selected.filter((id) => !pageIds.includes(id)) : pageIds,
      }
    }
    case CLEAR_VENDOR_SELECTION:
      return { ...state, selected: [] }

    default:
      return state
  }
}

export default function () {
  const initialCombined = combineReducers({ self: vendorProtectionReducer })

  return (state = initialCombined, _action) => state
}
